import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiUsers,
  FiUserPlus,
  FiCheckCircle,
  FiClock,
  FiTrendingUp,
  FiActivity,
  FiArrowRight,
  FiRefreshCw,
  FiAlertCircle,
} from "react-icons/fi";

import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
  Alert,
  Button,
} from "reactstrap";

import { useNavigate } from "react-router-dom";

import api from "../../utills/api";


/* =========================================================
   HELPERS
========================================================= */

const normalizeArray = (response) => {
  const data = response?.data?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.leads)) {
    return data.leads;
  }

  if (Array.isArray(data?.statuses)) {
    return data.statuses;
  }

  return [];
};


const getLeadStatus = (lead) => {
  return (
    lead?.status ||
    lead?.leadStatus ||
    "New"
  );
};


const getLeadName = (lead) => {
  return (
    lead?.name ||
    lead?.studentName ||
    lead?.firstName ||
    "Unnamed Lead"
  );
};


const getSource = (lead) => {
  return (
    lead?.source ||
    lead?.leadSource ||
    "Unknown"
  );
};


const getCreatedDate = (lead) => {
  return (
    lead?.createdAt ||
    lead?.createdDate ||
    null
  );
};


const formatDate = (date) => {
  if (!date) {
    return "-";
  }

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }

  return parsed.toLocaleDateString(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  );
};


const formatNumber = (value) => {
  return Number(value || 0).toLocaleString(
    "en-IN"
  );
};


/* =========================================================
   COMPONENT
========================================================= */

const Dashboard = () => {
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState("");


  /* =======================================================
     FETCH DASHBOARD DATA
  ======================================================= */

  const fetchDashboard = async (
    isRefresh = false
  ) => {
    try {
      setError("");

      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response =
        await api.get("/leads");

      const leadList =
        normalizeArray(response);

      setLeads(leadList);
    } catch (err) {
      console.error(
        "Dashboard fetch error:",
        err
      );

      setError(
        err?.response?.data?.message ||
        "Unable to load dashboard data."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };


  useEffect(() => {
    fetchDashboard();
  }, []);


  /* =======================================================
     DASHBOARD CALCULATIONS
  ======================================================= */

  const dashboard = useMemo(() => {
    const total =
      leads.length;

    const newLeads =
      leads.filter(
        (lead) =>
          getLeadStatus(lead)
            .toLowerCase() ===
          "new"
      ).length;

    const followUps =
      leads.filter(
        (lead) => {
          const status =
            getLeadStatus(
              lead
            ).toLowerCase();

          return (
            status === "follow-up" ||
            status === "follow up" ||
            status === "contacted"
          );
        }
      ).length;

    const converted =
      leads.filter(
        (lead) => {
          const status =
            getLeadStatus(
              lead
            ).toLowerCase();

          return (
            status === "converted" ||
            status === "admitted"
          );
        }
      ).length;

    const lost =
      leads.filter(
        (lead) =>
          getLeadStatus(lead)
            .toLowerCase() ===
          "lost"
      ).length;

    const conversionRate =
      total > 0
        ? (
            (converted /
              total) *
            100
          ).toFixed(1)
        : "0.0";

    /*
     * Today's leads
     */

    const today =
      new Date();

    const todayLeads =
      leads.filter(
        (lead) => {
          const created =
            getCreatedDate(
              lead
            );

          if (!created) {
            return false;
          }

          const date =
            new Date(created);

          return (
            date.getDate() ===
              today.getDate() &&
            date.getMonth() ===
              today.getMonth() &&
            date.getFullYear() ===
              today.getFullYear()
          );
        }
      ).length;


    /*
     * Source breakdown
     */

    const sourceMap = {};

    leads.forEach(
      (lead) => {
        const source =
          getSource(lead);

        sourceMap[source] =
          (sourceMap[source] || 0) +
          1;
      }
    );

    const sourceBreakdown =
      Object.entries(
        sourceMap
      )
        .sort(
          (a, b) =>
            b[1] - a[1]
        )
        .slice(0, 5);


    /*
     * Recent leads
     */

    const recentLeads =
      [...leads]
        .sort(
          (a, b) =>
            new Date(
              getCreatedDate(b) || 0
            ) -
            new Date(
              getCreatedDate(a) || 0
            )
        )
        .slice(0, 8);


    return {
      total,
      newLeads,
      followUps,
      converted,
      lost,
      conversionRate,
      todayLeads,
      sourceBreakdown,
      recentLeads,
    };
  }, [leads]);


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minHeight: "400px",
            }}
          >
            <Spinner size="sm" />
          </div>
        </Container>
      </div>
    );
  }

    document.title =
    "Dashboard | eduLeads.Online";

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="page-content">
      <Container fluid>

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="d-flex justify-content-between align-items-center mb-4">

          <div>
            <h4 className="mb-1">
              Dashboard
            </h4>

            <p className="text-muted mb-0">
              Education lead management
              overview
            </p>
          </div>

          <Button
            color="light"
            onClick={() =>
              fetchDashboard(true)
            }
            disabled={refreshing}
          >
            {refreshing ? (
              <Spinner
                size="sm"
                className="me-2"
              />
            ) : (
              <FiRefreshCw
                size={15}
                className="me-2"
              />
            )}

            Refresh
          </Button>

        </div>


        {/* =================================================
            ERROR
        ================================================= */}

        {error && (
          <Alert
            color="danger"
            className="d-flex align-items-center"
          >
            <FiAlertCircle
              className="me-2"
            />

            {error}
          </Alert>
        )}


        {/* =================================================
            KPI CARDS
        ================================================= */}

        <Row>

          {/* TOTAL LEADS */}

          <Col
            xl={3}
            md={6}
          >
            <Card className="card-animate">
              <CardBody>

                <div className="d-flex justify-content-between">

                  <div>
                    <p className="text-muted mb-2">
                      Total Leads
                    </p>

                    <h4 className="mb-0">
                      {formatNumber(
                        dashboard.total
                      )}
                    </h4>
                  </div>

                  <div
                    className="avatar-sm"
                  >
                    <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-20">
                      <FiUsers />
                    </span>
                  </div>

                </div>

                <div className="mt-3">
                  <span className="text-muted">
                    All education leads
                  </span>
                </div>

              </CardBody>
            </Card>
          </Col>


          {/* NEW LEADS */}

          <Col
            xl={3}
            md={6}
          >
            <Card className="card-animate">
              <CardBody>

                <div className="d-flex justify-content-between">

                  <div>
                    <p className="text-muted mb-2">
                      New Leads
                    </p>

                    <h4 className="mb-0">
                      {formatNumber(
                        dashboard.newLeads
                      )}
                    </h4>
                  </div>

                  <div
                    className="avatar-sm"
                  >
                    <span className="avatar-title rounded-circle bg-info-subtle text-info fs-20">
                      <FiUserPlus />
                    </span>
                  </div>

                </div>

                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() =>
                      navigate(
                        "/leads/new"
                      )
                    }
                  >
                    View new leads
                    <FiArrowRight
                      size={14}
                      className="ms-1"
                    />
                  </button>
                </div>

              </CardBody>
            </Card>
          </Col>


          {/* FOLLOW UPS */}

          <Col
            xl={3}
            md={6}
          >
            <Card className="card-animate">
              <CardBody>

                <div className="d-flex justify-content-between">

                  <div>
                    <p className="text-muted mb-2">
                      Follow-ups
                    </p>

                    <h4 className="mb-0">
                      {formatNumber(
                        dashboard.followUps
                      )}
                    </h4>
                  </div>

                  <div
                    className="avatar-sm"
                  >
                    <span className="avatar-title rounded-circle bg-warning-subtle text-warning fs-20">
                      <FiClock />
                    </span>
                  </div>

                </div>

                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() =>
                      navigate(
                        "/leads/follow-ups"
                      )
                    }
                  >
                    View follow-ups
                    <FiArrowRight
                      size={14}
                      className="ms-1"
                    />
                  </button>
                </div>

              </CardBody>
            </Card>
          </Col>


          {/* CONVERTED */}

          <Col
            xl={3}
            md={6}
          >
            <Card className="card-animate">
              <CardBody>

                <div className="d-flex justify-content-between">

                  <div>
                    <p className="text-muted mb-2">
                      Converted
                    </p>

                    <h4 className="mb-0">
                      {formatNumber(
                        dashboard.converted
                      )}
                    </h4>
                  </div>

                  <div
                    className="avatar-sm"
                  >
                    <span className="avatar-title rounded-circle bg-success-subtle text-success fs-20">
                      <FiCheckCircle />
                    </span>
                  </div>

                </div>

                <div className="mt-3">
                  <span className="text-success">
                    {dashboard.conversionRate}%
                  </span>

                  <span className="text-muted ms-1">
                    conversion rate
                  </span>
                </div>

              </CardBody>
            </Card>
          </Col>

        </Row>


        {/* =================================================
            SECONDARY SUMMARY
        ================================================= */}

        <Row>

          <Col xl={8}>

            <Card>
              <CardBody>

                <div className="d-flex justify-content-between align-items-center mb-4">

                  <div>
                    <h5 className="card-title mb-1">
                      Lead Overview
                    </h5>

                    <p className="text-muted mb-0">
                      Current lead pipeline
                    </p>
                  </div>

                  <FiTrendingUp
                    size={20}
                    className="text-muted"
                  />

                </div>


                <Row>

                  <Col md={3}>
                    <div className="border rounded p-3">
                      <p className="text-muted mb-1">
                        Today
                      </p>

                      <h5 className="mb-0">
                        {formatNumber(
                          dashboard.todayLeads
                        )}
                      </h5>
                    </div>
                  </Col>

                  <Col md={3}>
                    <div className="border rounded p-3">
                      <p className="text-muted mb-1">
                        New
                      </p>

                      <h5 className="mb-0">
                        {formatNumber(
                          dashboard.newLeads
                        )}
                      </h5>
                    </div>
                  </Col>

                  <Col md={3}>
                    <div className="border rounded p-3">
                      <p className="text-muted mb-1">
                        Converted
                      </p>

                      <h5 className="mb-0">
                        {formatNumber(
                          dashboard.converted
                        )}
                      </h5>
                    </div>
                  </Col>

                  <Col md={3}>
                    <div className="border rounded p-3">
                      <p className="text-muted mb-1">
                        Lost
                      </p>

                      <h5 className="mb-0">
                        {formatNumber(
                          dashboard.lost
                        )}
                      </h5>
                    </div>
                  </Col>

                </Row>

              </CardBody>
            </Card>

          </Col>


          {/* SOURCE BREAKDOWN */}

          <Col xl={4}>

            <Card>
              <CardBody>

                <h5 className="card-title mb-1">
                  Lead Sources
                </h5>

                <p className="text-muted mb-3">
                  Top sources by lead volume
                </p>

                {dashboard.sourceBreakdown
                  .length === 0 ? (
                  <div className="text-center text-muted py-4">
                    No lead source data
                  </div>
                ) : (
                  dashboard.sourceBreakdown.map(
                    ([source, count]) => (
                      <div
                        key={source}
                        className="d-flex justify-content-between align-items-center py-2 border-bottom"
                      >
                        <span>
                          {source}
                        </span>

                        <span className="badge bg-light text-dark">
                          {formatNumber(
                            count
                          )}
                        </span>
                      </div>
                    )
                  )
                )}

              </CardBody>
            </Card>

          </Col>

        </Row>


        {/* =================================================
            RECENT LEADS
        ================================================= */}

        <Row>

          <Col xl={12}>

            <Card>

              <CardBody>

                <div className="d-flex justify-content-between align-items-center mb-3">

                  <div>
                    <h5 className="card-title mb-1">
                      Recent Leads
                    </h5>

                    <p className="text-muted mb-0">
                      Latest leads received
                    </p>
                  </div>

                  <button
                    type="button"
                    className="btn btn-link text-decoration-none"
                    onClick={() =>
                      navigate(
                        "/leads"
                      )
                    }
                  >
                    View All
                    <FiArrowRight
                      size={14}
                      className="ms-1"
                    />
                  </button>

                </div>


                {dashboard.recentLeads
                  .length === 0 ? (

                  <div className="text-center py-5 text-muted">

                    <FiActivity
                      size={28}
                      className="mb-2"
                    />

                    <div>
                      No leads available.
                    </div>

                  </div>

                ) : (

                  <div className="table-responsive">

                    <table className="table table-hover align-middle mb-0">

                      <thead className="table-light">

                        <tr>
                          <th>
                            Lead
                          </th>

                          <th>
                            Source
                          </th>

                          <th>
                            Status
                          </th>

                          <th>
                            Created
                          </th>

                          <th>
                            Action
                          </th>
                        </tr>

                      </thead>

                      <tbody>

                        {dashboard.recentLeads.map(
                          (lead) => {

                            const status =
                              getLeadStatus(
                                lead
                              );

                            const statusClass =
                              status
                                .toLowerCase() ===
                              "converted"
                                ? "success"
                                : status
                                    .toLowerCase() ===
                                  "lost"
                                ? "danger"
                                : status
                                    .toLowerCase() ===
                                  "new"
                                ? "primary"
                                : "warning";

                            return (
                              <tr
                                key={
                                  lead._id ||
                                  lead.id
                                }
                              >

                                <td>

                                  <div className="fw-medium">
                                    {getLeadName(
                                      lead
                                    )}
                                  </div>

                                  <div className="text-muted small">
                                    {lead.email ||
                                      lead.phone ||
                                      lead.mobile ||
                                      "-"}
                                  </div>

                                </td>

                                <td>
                                  {getSource(
                                    lead
                                  )}
                                </td>

                                <td>

                                  <span
                                    className={`badge bg-${statusClass}-subtle text-${statusClass}`}
                                  >
                                    {status}
                                  </span>

                                </td>

                                <td>
                                  {formatDate(
                                    getCreatedDate(
                                      lead
                                    )
                                  )}
                                </td>

                                <td>

                                  <Button
                                    color="light"
                                    size="sm"
                                    onClick={() =>
                                      navigate(
                                        `/leads/${
                                          lead._id ||
                                          lead.id
                                        }`
                                      )
                                    }
                                  >
                                    View
                                  </Button>

                                </td>

                              </tr>
                            );
                          }
                        )}

                      </tbody>

                    </table>

                  </div>

                )}

              </CardBody>

            </Card>

          </Col>

        </Row>

      </Container>
    </div>
  );
};

export default Dashboard;